import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plug } from 'lucide-react';
import { toast } from 'sonner';
import { useConnectService, useDecideApproval, useReconnectService } from '../../../hooks/mutations';
import { goSignIn } from '../BotConnections';
import { desktop } from '../../../native/desktop';
import { connectInDesktop } from '../../../lib/desktopSignIn';
import { CardShell, ResolvedCard, ghostBtn, outcomeOf, primaryBtn, secondaryBtn } from './CardShell';

/**
 * The bot needs an app connected (agents/cards.py connect_card). One click signs in where the
 * app uses OAuth; a key or a program is entered in the bot's Apps tab, which the card opens.
 * When the person is back, Continue lets the bot go on with the app's tools. A card naming one of
 * the bot's connections (an MCP server it added that waits for a sign-in, or whose sign-in expired)
 * signs in to that connection again.
 */
const ConnectCard = ({ approval, botName, compact }) => {
  const { t } = useTranslation();
  const decide = useDecideApproval();
  const connect = useConnectService();
  const reconnect = useReconnectService();
  const card = approval.card;

  if (approval.status !== 'pending') {
    const connected = approval.status === 'approved' && /is connected/.test(approval.result || '');
    return (
      <ResolvedCard icon={Plug} testid="connect-card-resolved" ok={connected} text={card.app}
        outcome={outcomeOf(approval, t, t(connected ? 'bots.cards.connect_done' : 'bots.cards.connect_not_yet'))} />
    );
  }

  const busy = decide.isPending || connect.isPending || reconnect.isPending;
  const send = (approve) => decide.mutate({ approvalId: approval.id, approve }, {
    onError: (err) => toast.error(err?.response?.data?.detail || t('bots.cards.failed')),
  });
  // A service for desktop apps only (Upwork): signed in through the desktop app, never on the web.
  const desktopOnly = !!card.desktop_only;
  const inDesktop = desktop.canSignInForApps();
  const signIn = () => {
    if (desktopOnly) {
      connectInDesktop({ botId: approval.bot_id, catalogId: card.catalog_id, sharing: 'personal',
        doneText: t('bots.connections.desktop_done') })
        .then(() => send(true))
        .catch((err) => toast.error(err?.message === 'denied' ? t('bots.connections.desktop_denied', { name: card.app })
          : err?.response?.data?.detail?.message || err?.response?.data?.detail || t('bots.cards.failed')));
      return;
    }
    if (card.by_hand) {
      window.dispatchEvent(new CustomEvent('bb:open-bot-tab', { detail: 'apps' }));
      return;
    }
    const onError = (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || t('bots.cards.failed'));
    };
    if (card.connection_id) {
      reconnect.mutate(card.connection_id, {
        onSuccess: (res) => (res.authorize_url ? goSignIn(res.authorize_url) : send(true)), onError,
      });
      return;
    }
    connect.mutate({ botId: approval.bot_id, catalog_id: card.catalog_id, sharing: 'personal' }, {
      onSuccess: (res) => (res.authorize_url ? goSignIn(res.authorize_url) : send(true)),
      onError: (err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : detail?.message || t('bots.cards.failed'));
      },
    });
  };

  return (
    <CardShell icon={Plug} testid="connect-card" waiting compact={compact}
      eyebrow={t('bots.cards.connect_eyebrow', { bot: botName })}
      title={t('bots.cards.connect_title', { app: card.app })}
      footer={(
        <>
          {!(desktopOnly && !inDesktop) && (
            <button type="button" className={primaryBtn} onClick={signIn} disabled={busy} data-testid="connect-card-sign-in">
              <Plug className="w-3.5 h-3.5" />{t(card.by_hand ? 'bots.cards.connect_open_apps' : 'bots.cards.connect_sign_in', { app: card.app })}
            </button>
          )}
          <span className="flex-1" />
          <button type="button" className={ghostBtn} onClick={() => send(false)} disabled={busy} data-testid="card-decline">
            {t('bots.cards.not_now')}
          </button>
          <button type="button" className={secondaryBtn} onClick={() => send(true)} disabled={busy} data-testid="card-confirm">
            {t('bots.cards.connect_continue')}
          </button>
        </>
      )}>
      {card.reason && <p className="text-[13px] text-foreground/85">{card.reason}</p>}
      {desktopOnly && !inDesktop && (
        <p className="mt-1.5 text-[13px] text-amber-700 dark:text-amber-300" data-testid="connect-card-needs-desktop">
          {t('bots.connections.needs_desktop', { name: card.app })}
        </p>
      )}
    </CardShell>
  );
};

export default ConnectCard;
